import { ArrowLeft, User, Save, Loader2, RefreshCw } from 'lucide-react'
import { useState, useEffect } from 'react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useTravelRule, useUpdateTravelRule } from '@/hooks/useCpn'
import { logger } from '@/utils/logger'

interface PersonalInfoManagementScreenProps {
  onBack: () => void
}

interface TravelRuleMetadata {
  ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER: string
  ORIGINATOR_NAME: string
  [key: string]: string
}

interface FormErrors {
  [key: string]: string | undefined
}

const PersonalInfoManagementScreen = ({
  onBack,
}: PersonalInfoManagementScreenProps) => {
  const {
    data: travelRuleData,
    isLoading: isLoadingTravelRule,
    refetch,
    error: fetchError,
  } = useTravelRule()
  const updateTravelRuleMutation = useUpdateTravelRule()

  const [formData, setFormData] = useState<TravelRuleMetadata>({
    ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER: '',
    ORIGINATOR_NAME: '',
  })
  const [isRefreshing, setIsRefreshing] = useState(false)

  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})
  const [success, setSuccess] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Load existing travel rule data when component mounts or data changes
  useEffect(() => {
    if (travelRuleData?.travelRule?.metadata) {
      const existingData = travelRuleData.travelRule.metadata
      setFormData({
        ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER:
          existingData.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER || '',
        ORIGINATOR_NAME: existingData.ORIGINATOR_NAME || '',
      })
    }
  }, [travelRuleData])

  // Validation function
  const validateField = (
    name: keyof TravelRuleMetadata,
    value: string
  ): string | undefined => {
    if (!value.trim()) return 'This field is required'

    switch (name) {
      case 'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER':
        if (value.length < 5) return 'ID number is too short'
        if (value.length > 50) return 'ID number is too long'
        break
      case 'ORIGINATOR_NAME':
        if (value.length < 2) return 'Name is too short'
        if (value.length > 100) return 'Name is too long'
        break
    }
    return undefined
  }

  const handleInputChange = (name: keyof TravelRuleMetadata, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }))

    // Clear any previous success/error messages
    setSuccess(null)
    setError(null)

    // Validate on change if field has been touched
    if (touched[name]) {
      const error = validateField(name, value)
      setErrors((prev) => ({ ...prev, [name]: error }))
    }
  }

  const handleInputBlur = (name: keyof TravelRuleMetadata) => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    const error = validateField(name, formData[name])
    setErrors((prev) => ({ ...prev, [name]: error }))
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}
    let isValid = true

    ;(Object.keys(formData) as Array<keyof TravelRuleMetadata>).forEach(
      (key) => {
        const error = validateField(key, formData[key])
        if (error) {
          newErrors[key] = error
          isValid = false
        }
      }
    )

    setErrors(newErrors)
    setTouched(
      Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {})
    )
    return isValid
  }

  const handleRefresh = async () => {
    setIsRefreshing(true)
    setError(null)
    try {
      await refetch()
    } catch (err) {
      logger.error('Failed to refresh travel rule data', err)
      setError('Failed to refresh data. Please try again.')
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      return
    }

    setError(null)
    setSuccess(null)

    try {
      // Prepare metadata by filtering out empty values
      const metadata = Object.fromEntries(
        Object.entries(formData).filter(([_, value]) => value.trim() !== '')
      )

      await updateTravelRuleMutation.mutateAsync({ metadata })

      setSuccess('Personal information updated successfully!')

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null)
      }, 3000)
    } catch (err) {
      logger.error('Failed to update personal information', err)
      const errorObj = err as {
        response?: { data?: { message?: string } }
        message?: string
      }
      setError(
        errorObj.response?.data?.message ||
          errorObj.message ||
          'Failed to update personal information. Please try again.'
      )
    }
  }

  // Check if form has any errors or empty required fields
  const hasErrors = Object.values(errors).some((error) => error)
  const hasEmptyFields = Object.values(formData).some((value) => !value.trim())
  const isSubmitting = updateTravelRuleMutation.isPending
  const canSubmit =
    !hasErrors && !hasEmptyFields && !isSubmitting && !isLoadingTravelRule

  // Show loading state while initially fetching data
  if (isLoadingTravelRule) {
    return (
      <div className="h-[600px] flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Loading personal information...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[600px]">
      <div className="p-4 space-y-4 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="p-1">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                Personal Information
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Update your identity information
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing || isSubmitting}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800"
            title="Refresh data"
          >
            <RefreshCw
              className={`w-4 h-4 text-gray-500 ${isRefreshing ? 'animate-spin' : ''}`}
            />
          </Button>
        </div>

        {/* Success Message */}
        {success && (
          <Card className="p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700">
            <p className="text-sm text-green-600 dark:text-green-300">
              {success}
            </p>
          </Card>
        )}

        {/* Error Message */}
        {(error || fetchError) && (
          <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700">
            <p className="text-sm text-red-600 dark:text-red-300">
              {error || (fetchError as any)?.message || 'An error occurred'}
            </p>
          </Card>
        )}

        {/* Identity Information Form */}
        <Card className="p-4">
          <div className="flex items-center space-x-2 mb-4">
            <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Identity Information
            </h3>
          </div>

          <div className="space-y-4">
            <div>
              <Label
                htmlFor="nationalId"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Sender National ID *
              </Label>
              <Input
                id="nationalId"
                value={formData.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER}
                onChange={(e) =>
                  handleInputChange(
                    'ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER',
                    e.target.value
                  )
                }
                onBlur={() =>
                  handleInputBlur('ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER')
                }
                placeholder="Enter your national ID number"
                className={
                  errors.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER
                    ? 'border-red-500'
                    : ''
                }
                disabled={isSubmitting}
              />
              {errors.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {errors.ORIGINATOR_NATIONAL_IDENTIFICATION_NUMBER}
                </p>
              )}
            </div>

            <div>
              <Label
                htmlFor="originatorName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                Sender Full Name *
              </Label>
              <Input
                id="originatorName"
                value={formData.ORIGINATOR_NAME}
                onChange={(e) =>
                  handleInputChange('ORIGINATOR_NAME', e.target.value)
                }
                onBlur={() => handleInputBlur('ORIGINATOR_NAME')}
                placeholder="Enter your full legal name"
                className={errors.ORIGINATOR_NAME ? 'border-red-500' : ''}
                disabled={isSubmitting}
              />
              {errors.ORIGINATOR_NAME && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                  {errors.ORIGINATOR_NAME}
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Submit Button */}
      <div className="p-4 border-t border-gray-100 dark:border-gray-700">
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="w-full bg-green-600 hover:bg-green-700 text-white h-12 text-sm font-medium rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="animate-spin rounded-full h-4 w-4" />
              <span>Updating Information...</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <Save className="w-4 h-4" />
              <span>Save Personal Information</span>
            </div>
          )}
        </Button>
      </div>
    </div>
  )
}

export default PersonalInfoManagementScreen
